import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import SkillsSelection from "../components/SkillsSelection";
import useNavigationWarning from '../hooks/useNavigationWarning';

const SkillsSelectionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Move all useState declarations to the top level, before any conditional logic
  const [navigationTarget, setNavigationTarget] = useState(null);
  const { 
    topSkillsData, 
    resumeDataBeforeSkills, 
    metrics,
    similarJobTitles,
    isStreamlineFlow = true,
    userEmail,
    emailSendToken, 
  } = location.state || {};

  // Warn users before leaving the skills selection page (allow forward to results)
  useNavigationWarning(true, 'You haven\'t finished selecting skills. Are you sure you want to leave?', {
    allowPaths: ['/results'],
  });

  // Place useEffect after all useState declarations but before any other code
  useEffect(() => {
    if (navigationTarget) {
      const { path, stateData } = navigationTarget;
      navigate(path, { state: stateData });
      setNavigationTarget(null);
    }
  }, [navigationTarget, navigate]);

  useEffect(() => {
    // Add a style tag directly to the document head
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      .app-container {
        background: linear-gradient(180deg, #FFFFFF 0%, #F0FBF3 35%, #E2F5E0 100%) !important;
        background-image: linear-gradient(180deg, #FFFFFF 0%, #F0FBF3 35%, #E2F5E0 100%) !important;
        background-attachment: fixed !important;
      }
      
      .app-container::before,
      .app-container::after {
        display: none !important;
      }
    `;
    document.head.appendChild(styleElement);

    // Clean up when component unmounts
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  if (!topSkillsData || !resumeDataBeforeSkills) {
    navigate('/');
    return null;
  }

  const handleAddSkills = (selectedSkills) => {
    console.log("Add Skills button clicked");
    const updatedResumeData = { ...resumeDataBeforeSkills };
    
    // Helper function to safely check if a skill is in a category
    const isSkillInCategory = (skill, categoryName) => {
      const category = topSkillsData.missing_skills_by_category?.[categoryName];
      if (!Array.isArray(category) || category.length === 0) return false;
      
      // Handle both object format and string format
      return category.some(item => 
        (typeof item === 'object' && item.skill === skill) || 
        (typeof item === 'string' && item === skill)
      );
    };
    
    // Only map skills to technical categories if this is a tech job
    if (resumeDataBeforeSkills.is_tech_job) {
      const programmingLanguages = selectedSkills.filter(skill => 
        isSkillInCategory(skill, "Programming Languages")
      );
      
      const applications = selectedSkills.filter(skill => 
        isSkillInCategory(skill, "Applications")
      );
      
      const technicalSkills = selectedSkills.filter(skill => 
        isSkillInCategory(skill, "Technical Skills")
      );
      
      const otherSkills = selectedSkills.filter(skill => 
        !isSkillInCategory(skill, "Programming Languages") &&
        !isSkillInCategory(skill, "Applications") &&
        !isSkillInCategory(skill, "Technical Skills")
      );
      
      if (programmingLanguages.length > 0) {
        const existing = updatedResumeData.programming_languages || '';
        updatedResumeData.programming_languages = existing ? 
          `${existing}, ${programmingLanguages.join(', ')}` : 
          programmingLanguages.join(', ');
      }
      
      if (applications.length > 0) {
        const existing = updatedResumeData.applications || '';
        updatedResumeData.applications = existing ? 
          `${existing}, ${applications.join(', ')}` : 
          applications.join(', ');
      }
      
      if (technicalSkills.length > 0) {
        const existing = updatedResumeData.technical_skills || '';
        updatedResumeData.technical_skills = existing ? 
          `${existing}, ${technicalSkills.join(', ')}` : 
          technicalSkills.join(', ');
      }
      
      if (otherSkills.length > 0) {
        const existing = updatedResumeData.technical_skills || '';
        updatedResumeData.technical_skills = existing ? 
          `${existing}, ${otherSkills.join(', ')}` : 
          otherSkills.join(', ');
      }
    } else {
      // For non-tech jobs, map skills to their respective categories
      const technicalSkills = selectedSkills.filter(skill => 
        isSkillInCategory(skill, "Technical Skills")
      );
      
      const nonTechnicalSkills = selectedSkills.filter(skill => 
        isSkillInCategory(skill, "Non-Technical Skills")
      );
      
      const domainExperience = selectedSkills.filter(skill => 
        isSkillInCategory(skill, "Domain Experience")
      );
      
      if (technicalSkills.length > 0) {
        const existing = updatedResumeData.technical_skills || '';
        updatedResumeData.technical_skills = existing ? 
          `${existing}, ${technicalSkills.join(', ')}` : 
          technicalSkills.join(', ');
      }
      
      if (nonTechnicalSkills.length > 0) {
        const existing = updatedResumeData.non_technical_skills || '';
        updatedResumeData.non_technical_skills = existing ? 
          `${existing}, ${nonTechnicalSkills.join(', ')}` : 
          nonTechnicalSkills.join(', ');
      }
      
      if (domainExperience.length > 0) {
        const existing = updatedResumeData.domain_experience || '';
        updatedResumeData.domain_experience = existing ? 
          `${existing}, ${domainExperience.join(', ')}` : 
          domainExperience.join(', ');
      }
    }
    
    // Go directly to results for all job types
    setNavigationTarget({
      path: '/results',
      stateData: {
        resumeData: updatedResumeData,
        metrics: metrics,
        similarJobTitles: similarJobTitles,
        jobTitle: similarJobTitles?.[0] || '',
        userEmail: userEmail,
        emailSendToken: location.state.emailSendToken, 
      }
    });
  };

  const handleSkipSkillsSelection = () => {
    console.log("Skip button clicked");
    
    // Go directly to results for all job types
    setNavigationTarget({
      path: '/results',
      stateData: {
        resumeData: resumeDataBeforeSkills,
        metrics: metrics,
        similarJobTitles: similarJobTitles,
        jobTitle: similarJobTitles?.[0] || '',
        userEmail: userEmail,
        emailSendToken: location.state.emailSendToken,
      }
    });
  };

  // Helper function to normalize skill item to object format with percentage
  const normalizeSkill = (item) => {
    if (typeof item === 'object' && item.skill) {
      return { skill: item.skill, percentage: item.percentage ?? 0 };
    }
    return { skill: item, percentage: 0 };
  };

  const isAllowedSkill = (item) => {
    const skillName = typeof item === 'object' && item.skill ? item.skill : item;
    return skillName !== 'Time Off Management';
  };

  // Calculate totalMissing excluding soft skills for tech jobs
  const softSkillsCount = resumeDataBeforeSkills?.is_tech_job
    ? (topSkillsData?.missing_skills_by_category?.["Non-Technical Skills"]?.length || 0)
    : 0;
  const adjustedTotalMissing = (topSkillsData?.total_missing ?? 0) - softSkillsCount;

  // In SkillsSelectionPage.js
  return (
    <div className="homepage">  
      <SkillsSelection
        categorizedSkills={
          resumeDataBeforeSkills?.is_tech_job 
            ? {
                "Programming Languages": Array.isArray(topSkillsData?.missing_skills_by_category?.["Programming Languages"]) 
                  ? topSkillsData.missing_skills_by_category["Programming Languages"].filter(isAllowedSkill).map(normalizeSkill) 
                  : [],
                "Applications": Array.isArray(topSkillsData?.missing_skills_by_category?.["Applications"]) 
                  ? topSkillsData.missing_skills_by_category["Applications"].filter(isAllowedSkill).map(normalizeSkill) 
                  : [],
                "Technical Skills": Array.isArray(topSkillsData?.missing_skills_by_category?.["Technical Skills"]) 
                  ? topSkillsData.missing_skills_by_category["Technical Skills"].filter(isAllowedSkill).map(normalizeSkill) 
                  : []
              }
            : {
                "Technical Skills": Array.isArray(topSkillsData?.missing_skills_by_category?.["Technical Skills"]) 
                  ? topSkillsData.missing_skills_by_category["Technical Skills"].filter(isAllowedSkill).map(normalizeSkill) 
                  : [],
                "Non-Technical Skills": Array.isArray(topSkillsData?.missing_skills_by_category?.["Non-Technical Skills"]) 
                  ? topSkillsData.missing_skills_by_category["Non-Technical Skills"].filter(isAllowedSkill).map(normalizeSkill) 
                  : [],
                "Domain Experience": Array.isArray(topSkillsData?.missing_skills_by_category?.["Domain Experience"]) 
                  ? topSkillsData.missing_skills_by_category["Domain Experience"].filter(isAllowedSkill).map(normalizeSkill) 
                  : []
              }
        }
        onAddSkills={handleAddSkills}
        isTechJob={resumeDataBeforeSkills?.is_tech_job}
        onSkip={handleSkipSkillsSelection}
  
        // Initialize keyword score (excluding soft skills for tech jobs)
        totalMissing={adjustedTotalMissing}
        totalPresent={topSkillsData?.total_present ?? 0}
        jobTitle={similarJobTitles?.[0] || ''}
        showPercentages={isStreamlineFlow}
        isStreamlineFlow={isStreamlineFlow}
      />
    </div>
  );
};

export default SkillsSelectionPage;
